'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { Users, RefreshCw, TrendingUp, TrendingDown, Minus, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line, CartesianGrid } from 'recharts';
import { createClient } from '@/lib/supabase/client';
import Modal from '@/components/ui/Modal';
import Badge, { gradeVariant } from '@/components/ui/Badge';

interface GradeData {
  grade: string;
  count: number;
  color: string;
}

const GRADE_COLORS: Record<string, string> = {
  'A+': '#22c55e',
  'A': '#84cc16',
  'B': '#3b82f6',
  'C': '#f59e0b',
  'D': '#ef4444',
};

interface CrewBreakdownMember {
  id: string;
  name: string;
  crewCode: string;
  role: string;
  depot: string;
}

interface CrewDetailStats {
  member: CrewBreakdownMember;
  recentDuties: { dutyCode: string; route: string; date: string; status: string; shift: string }[];
  onTimeRate: number;
  absenceCount: number;
  gradeTrend: { week: string; rate: number }[];
  totalDuties: number;
  completedDuties: number;
}

function GradeTrendChart({ data }: { data: { week: string; rate: number }[] }) {
  if (data.length === 0) return <p className="text-xs text-muted-foreground text-center py-4">No trend data available</p>;
  return (
    <ResponsiveContainer width="100%" height={100}>
      <LineChart data={data} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis dataKey="week" tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} />
        <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
        <Tooltip
          contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11 }}
          formatter={(v: number) => [`${v}%`, 'On-Time Rate']}
        />
        <Line type="monotone" dataKey="rate" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3, fill: '#3b82f6' }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function CrewDetailCard({ stats, onClose }: { stats: CrewDetailStats; onClose: () => void }) {
  const trendData = stats.gradeTrend;
  const lastTwo = trendData.slice(-2);
  const trendDir = lastTwo.length === 2
    ? lastTwo[1].rate > lastTwo[0].rate ? 'up' : lastTwo[1].rate < lastTwo[0].rate ? 'down' : 'flat'
    : 'flat';

  return (
    <div className="space-y-5">
      {/* Header info */}
      <div className="flex items-center gap-3 p-4 rounded-xl bg-muted/30 border border-border">
        <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
          <Users size={18} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-700 text-foreground text-sm">{stats.member.name}</p>
          <p className="text-xs text-muted-foreground">{stats.member.crewCode} · {stats.member.role} · {stats.member.depot}</p>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 rounded-xl bg-positive/8 border border-positive/20 text-center">
          <p className="text-2xl font-800 text-positive tabular-nums">{stats.onTimeRate}%</p>
          <p className="text-[10px] font-600 uppercase tracking-wider text-muted-foreground mt-0.5">On-Time Rate</p>
        </div>
        <div className="p-3 rounded-xl bg-warning/8 border border-warning/20 text-center">
          <p className="text-2xl font-800 text-warning tabular-nums">{stats.absenceCount}</p>
          <p className="text-[10px] font-600 uppercase tracking-wider text-muted-foreground mt-0.5">Absences</p>
        </div>
        <div className="p-3 rounded-xl bg-primary/8 border border-primary/20 text-center">
          <p className="text-2xl font-800 text-primary tabular-nums">{stats.completedDuties}/{stats.totalDuties}</p>
          <p className="text-[10px] font-600 uppercase tracking-wider text-muted-foreground mt-0.5">Duties Done</p>
        </div>
      </div>

      {/* Grade trend */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-700 uppercase tracking-wider text-muted-foreground">On-Time Trend — Last 30 Days</p>
          <div className="flex items-center gap-1 text-xs font-600">
            {trendDir === 'up' && <><TrendingUp size={13} className="text-positive" /><span className="text-positive">Improving</span></>}
            {trendDir === 'down' && <><TrendingDown size={13} className="text-negative" /><span className="text-negative">Declining</span></>}
            {trendDir === 'flat' && <><Minus size={13} className="text-muted-foreground" /><span className="text-muted-foreground">Stable</span></>}
          </div>
        </div>
        <GradeTrendChart data={stats.gradeTrend} />
      </div>

      {/* Recent duties */}
      <div>
        <p className="text-xs font-700 uppercase tracking-wider text-muted-foreground mb-2">Recent Duties</p>
        {stats.recentDuties.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3">No recent duties found</p>
        ) : (
          <div className="space-y-1.5 max-h-[200px] overflow-y-auto scrollbar-thin pr-1">
            {stats.recentDuties.map((d, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/20 border border-border text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  {d.status === 'Completed' ? (
                    <CheckCircle2 size={12} className="text-positive shrink-0" />
                  ) : d.status === 'Cancelled' ? (
                    <XCircle size={12} className="text-negative shrink-0" />
                  ) : (
                    <Clock size={12} className="text-warning shrink-0" />
                  )}
                  <span className="font-600 text-foreground truncate">{d.dutyCode}</span>
                  <span className="text-muted-foreground truncate">· {d.route}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <span className="text-muted-foreground">{d.shift}</span>
                  <span className="text-muted-foreground tabular-nums">{d.date}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface GradeBreakdownModalProps {
  grade: string | null;
  onClose: () => void;
}

function GradeBreakdownModal({ grade, onClose }: GradeBreakdownModalProps) {
  const [crewList, setCrewList] = useState<CrewBreakdownMember[]>([]);
  const [selectedCrew, setSelectedCrew] = useState<CrewDetailStats | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (!grade) return;
    setCrewList([]);
    setSelectedCrew(null);
    setLoadingList(true);

    const fetchCrew = async () => {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('crew_members')
        .select('id, crew_code, name, role, depot')
        .eq('grade', grade)
        .order('name', { ascending: true });

      if (!error && data) {
        setCrewList(data.map((r: any) => ({
          id: r.id,
          name: r.name,
          crewCode: r.crew_code,
          role: r.role,
          depot: r.depot,
        })));
      }
      setLoadingList(false);
    };

    fetchCrew();
  }, [grade]);

  const fetchCrewDetail = useCallback(async (member: CrewBreakdownMember) => {
    setLoadingDetail(true);
    const supabase = createClient();

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const fromDate = thirtyDaysAgo.toISOString().split('T')[0];
    const today = new Date().toISOString().split('T')[0];

    // Fetch recent duties for this crew member (as driver or conductor)
    const { data: dutiesRaw } = await supabase
      .from('duty_assignments')
      .select('duty_code, route, route_name, duty_date, status, shift')
      .or(`driver1_id.eq.${member.id},conductor_id.eq.${member.id}`)
      .gte('duty_date', fromDate)
      .lte('duty_date', today)
      .order('duty_date', { ascending: false })
      .limit(20);

    const duties = (dutiesRaw ?? []).map((d: any) => ({
      dutyCode: d.duty_code,
      route: d.route,
      date: d.duty_date,
      status: d.status,
      shift: d.shift,
    }));

    const totalDuties = duties.length;
    const completedDuties = duties.filter((d) => d.status === 'Completed').length;
    const cancelledDuties = duties.filter((d) => d.status === 'Cancelled').length;
    const absenceCount = cancelledDuties;
    const onTimeRate = totalDuties > 0 ? Math.round((completedDuties / totalDuties) * 100) : 0;

    // Build weekly trend over last 4 weeks
    const gradeTrend: { week: string; rate: number }[] = [];
    for (let w = 3; w >= 0; w--) {
      const wStart = new Date();
      wStart.setDate(wStart.getDate() - (w + 1) * 7);
      const wEnd = new Date();
      wEnd.setDate(wEnd.getDate() - w * 7);
      const wStartStr = wStart.toISOString().split('T')[0];
      const wEndStr = wEnd.toISOString().split('T')[0];

      const weekDuties = duties.filter((d) => d.date >= wStartStr && d.date < wEndStr);
      const weekCompleted = weekDuties.filter((d) => d.status === 'Completed').length;
      const weekRate = weekDuties.length > 0 ? Math.round((weekCompleted / weekDuties.length) * 100) : 0;

      const label = `W${4 - w}`;
      gradeTrend.push({ week: label, rate: weekRate });
    }

    setSelectedCrew({
      member,
      recentDuties: duties.slice(0, 10),
      onTimeRate,
      absenceCount,
      gradeTrend,
      totalDuties,
      completedDuties,
    });
    setLoadingDetail(false);
  }, []);

  if (!grade) return null;

  const gradeColor = GRADE_COLORS[grade] ?? '#6b7280';

  return (
    <Modal
      open={!!grade}
      onClose={onClose}
      title=""
      size="lg"
    >
      <div className="p-5">
        {/* Grade header */}
        <div className="flex items-center gap-3 mb-5">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center text-white text-xl font-800 shadow-sm shrink-0"
            style={{ background: gradeColor }}
          >
            {grade}
          </div>
          <div>
            <h2 className="text-lg font-700 text-foreground">Grade {grade} Crew Breakdown</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {loadingList ? 'Loading…' : `${crewList.length} crew member${crewList.length !== 1 ? 's' : ''} · Last 30 days`}
            </p>
          </div>
        </div>

        {selectedCrew ? (
          <div>
            <button
              onClick={() => setSelectedCrew(null)}
              className="flex items-center gap-1.5 text-xs font-600 text-primary hover:text-primary/80 mb-4 transition-colors"
            >
              ← Back to crew list
            </button>
            <CrewDetailCard stats={selectedCrew} onClose={() => setSelectedCrew(null)} />
          </div>
        ) : (
          <div>
            {loadingList ? (
              <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
                <RefreshCw size={16} className="animate-spin" />
                <span className="text-sm">Loading crew…</span>
              </div>
            ) : crewList.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2 text-center">
                <Users size={28} className="text-muted-foreground/40" />
                <p className="text-sm font-600 text-muted-foreground">No crew with grade {grade}</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs font-600 text-muted-foreground mb-3">Click a crew member to see their detailed performance breakdown</p>
                {crewList.map((member) => (
                  <button
                    key={member.id}
                    onClick={() => fetchCrewDetail(member)}
                    disabled={loadingDetail}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-border bg-card hover:bg-muted/30 hover:border-primary/30 transition-all duration-150 text-left group disabled:opacity-60"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <Users size={14} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-600 text-foreground group-hover:text-primary transition-colors">{member.name}</p>
                        <p className="text-xs text-muted-foreground">{member.crewCode} · {member.role} · {member.depot}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={gradeVariant(grade)}>{grade}</Badge>
                      {loadingDetail ? (
                        <RefreshCw size={14} className="text-muted-foreground animate-spin" />
                      ) : (
                        <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors">View →</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default function PerformanceBarChart() {
  const [data, setData] = useState<GradeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const supabase = createClient();

      const { data: rows, error } = await supabase
        .from('crew_members')
        .select('grade');

      if (error || !rows) {
        setLoading(false);
        return;
      }

      const counts: Record<string, number> = { 'A+': 0, 'A': 0, 'B': 0, 'C': 0, 'D': 0 };
      rows.forEach((r) => {
        const g = r.grade as string;
        if (g && counts[g] !== undefined) counts[g] += 1;
        else if (g) counts[g] = (counts[g] ?? 0) + 1;
      });

      const result: GradeData[] = Object.entries(counts)
        .filter(([, count]) => count > 0)
        .map(([grade, count]) => ({
          grade,
          count,
          color: GRADE_COLORS[grade] ?? '#6b7280',
        }));

      setData(result);
      setLoading(false);
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[200px] gap-3 text-center">
        <RefreshCw size={18} className="text-muted-foreground animate-spin" />
        <p className="text-xs text-muted-foreground">Loading performance data…</p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[200px] gap-3 text-center">
        <div className="w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center">
          <Users size={18} className="text-muted-foreground" />
        </div>
        <div>
          <p className="text-sm font-600 text-muted-foreground">No performance data</p>
          <p className="text-xs text-muted-foreground/70 mt-0.5">Grade distribution will appear once crew data is available</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <p className="text-[10px] text-muted-foreground mb-2 text-center">Click a grade bar to see crew breakdown</p>
      <ResponsiveContainer width="100%" height={190}>
        <BarChart
          data={data}
          margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
          barSize={28}
          style={{ cursor: 'pointer' }}
        >
          <XAxis
            dataKey="grade"
            tick={{ fontSize: 12, fill: 'var(--muted-foreground)', fontWeight: 600 }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              background: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 12,
            }}
            labelStyle={{ color: 'var(--foreground)', fontWeight: 600 }}
            formatter={(value: number) => [`${value} crew`, 'Count']}
            cursor={{ fill: 'rgba(0,0,0,0.05)' }}
          />
          <Bar
            dataKey="count"
            name="Crew"
            radius={[4, 4, 0, 0]}
            onClick={(entry) => setSelectedGrade(entry.grade as string)}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color}
                opacity={selectedGrade && selectedGrade !== entry.grade ? 0.5 : 1}
                style={{ cursor: 'pointer' }}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <GradeBreakdownModal
        grade={selectedGrade}
        onClose={() => setSelectedGrade(null)}
      />
    </>
  );
}